import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getCuratedCollections,
  saveCuratedCollection,
  deleteCuratedCollection,
  getWatchBrands,
  getLookupCollection,
  uploadCuratedCollectionImage,
  getArticleById,
} from '../../services/firebase';
import {
  getReferencesByObjectIds,
  searchGeneral,
} from '../../services/algolia';
import type {
  Article,
  CuratedCollection,
  WatchBrand,
  LookupValue,
  WatchReferenceAlgoliaHit,
} from '../../types';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Badge } from '../common/Badge';
import { RichTextEditor } from '../common/RichTextEditor';
import { useResolvedHitImage } from '../../hooks/useResolvedHitImage';
import {
  ReferencePickerModal,
  ReferenceThumbnail,
} from './ReferencePickerModal';
import {
  ArticlePickerModal,
  ArticleThumbnail,
  articleSubtitle,
} from './ArticlePickerModal';
import { buildFacetFilters, FACET_LABELS } from './facetFilters';
import type { FacetKey } from './facetFilters';
import {
  buildNumericFilters,
  collectionToEditor,
  editorToPayload,
  emptyEditor,
  moveReference,
  needsSearchFallback,
  validateEditor,
  LANDING_REFERENCE_CAP,
} from './collectionPayload';
import type { EditorState, ManualReference } from './collectionPayload';
import { getWhatsNewTitle } from '../../services/whatsNewConfig';

const LOOKUP_COLLECTION_BY_FACET: Partial<Record<FacetKey, string>> = {
  watchType: 'lookup_watch_types',
  occasion: 'lookup_watch_occasion',
  movementType: 'lookup_movement_types',
  caseMaterial: 'lookup_case_materials',
  dialColor: 'lookup_dial_colors',
};

export function CuratedCollectionsManager() {
  const [collections, setCollections] = useState<CuratedCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  /** The configured What's New title, for the card-name mismatch badge. */
  const [whatsNewTitle, setWhatsNewTitle] = useState<string | null>(null);

  useEffect(() => {
    getWhatsNewTitle()
      .then(setWhatsNewTitle)
      .catch((err) => console.warn("Failed to read the What's New title:", err));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getCuratedCollections();
      setCollections(data);
    } catch (err) {
      console.error('Failed to load collections:', err);
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return collections;
    return collections.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q),
    );
  }, [collections, searchTerm]);

  const handleNew = () => {
    const maxOrder = collections.reduce(
      (m, c) => Math.max(m, c.sortOrder ?? 0),
      -1,
    );
    setEditor({ ...emptyEditor, sortOrder: maxOrder + 1 });
  };

  const handleEdit = (c: CuratedCollection) => {
    setEditor(collectionToEditor(c));
  };

  const handleSave = async () => {
    if (!editor) return;
    let toSave = editor;
    if (editor.targetType === 'whatsNew') {
      // The name is locked to the configured title (memo Q8). Re-read it at
      // save time, so a title changed in another tab is not saved stale.
      try {
        const title = await getWhatsNewTitle();
        toSave = { ...editor, name: title };
        setWhatsNewTitle(title);
      } catch (err) {
        console.error("Failed to read the What's New title:", err);
        toast.error("Couldn't read the What's New title — try again");
        return;
      }
    }
    const { error, warning } = validateEditor(toSave);
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    try {
      const payload = editorToPayload(toSave);
      await saveCuratedCollection(toSave.id, payload);
      if (warning) toast(warning, { icon: '⚠️' });
      else toast.success(editor.id ? 'Collection updated' : 'Collection created');
      setEditor(null);
      await load();
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error('Failed to save collection');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c: CuratedCollection) => {
    if (!confirm(`Delete collection "${c.name}"? This cannot be undone.`)) return;
    setDeletingId(c.id);
    try {
      await deleteCuratedCollection(c.id);
      setCollections((prev) => prev.filter((x) => x.id !== c.id));
      toast.success(`Deleted "${c.name}"`);
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error('Failed to delete collection');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (c: CuratedCollection) => {
    setTogglingId(c.id);
    try {
      await saveCuratedCollection(c.id, { isActive: !c.isActive });
      setCollections((prev) =>
        prev.map((x) =>
          x.id === c.id ? { ...x, isActive: !c.isActive } : x,
        ),
      );
    } catch (err) {
      console.error('Failed to toggle:', err);
      toast.error('Failed to update');
    } finally {
      setTogglingId(null);
    }
  };

  const handleSortOrderChange = async (c: CuratedCollection, newOrder: number) => {
    setCollections((prev) =>
      prev
        .map((x) => (x.id === c.id ? { ...x, sortOrder: newOrder } : x))
        .sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999)),
    );
    try {
      await saveCuratedCollection(c.id, { sortOrder: newOrder });
    } catch (err) {
      console.error('Failed to update sort order:', err);
      toast.error('Failed to update order');
      await load();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Curated Collections</h1>
          <p className="text-gray-500 mt-1">
            Themed groupings of watches, either by filter (e.g., "Pilot watches") or manual reference IDs.
          </p>
        </div>
        <Button onClick={handleNew}>+ New Collection</Button>
      </div>

      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <input
            type="text"
            placeholder="Search collections…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">
                Order
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28">
                Active
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-48">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <input
                    type="number"
                    value={c.sortOrder ?? 0}
                    onChange={(e) =>
                      handleSortOrderChange(c, Number(e.target.value))
                    }
                    className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{c.name}</div>
                  {c.description && (
                    <div className="text-xs text-gray-500 truncate max-w-md">
                      {c.description}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    <Badge variant={c.type === 'filter' ? 'info' : 'default'}>
                      {c.type}
                    </Badge>
                    {c.targetType === 'landing' && (
                      <Badge variant="success">landing</Badge>
                    )}
                    {c.targetType === 'whatsNew' && (
                      <Badge variant="success">what&rsquo;s new</Badge>
                    )}
                    {c.targetType === 'whatsNew' &&
                      whatsNewTitle !== null &&
                      c.name !== whatsNewTitle && (
                        <Badge variant="warning">
                          name ≠ title “{whatsNewTitle}” — re-save
                        </Badge>
                      )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleToggleActive(c)}
                    disabled={togglingId === c.id}
                    className={`inline-flex items-center gap-2 text-xs px-2 py-1 rounded ${
                      c.isActive
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } disabled:opacity-50`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        c.isActive ? 'bg-green-500' : 'bg-gray-400'
                      }`}
                    />
                    {c.isActive ? 'Active' : 'Inactive'}
                  </button>
                  {c.testMode && (
                    <span className="ml-2 text-xs px-2 py-1 rounded bg-amber-100 text-amber-800">
                      Test
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(c)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(c)}
                      loading={deletingId === c.id}
                      className="text-red-600 hover:text-red-700"
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {searchTerm
              ? 'No collections match your search'
              : 'No collections yet — click "+ New Collection" to start.'}
          </div>
        )}
      </div>

      {editor && (
        <CollectionEditorModal
          otherWhatsNewCards={collections
            .filter((c) => c.targetType === 'whatsNew' && c.id !== editor.id)
            .map((c) => c.name || c.id)}
          state={editor}
          onChange={setEditor}
          onClose={() => setEditor(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
}

interface EditorModalProps {
  /** Names of OTHER collections already targeting What's New — more than one is a mistake. */
  otherWhatsNewCards?: string[];
  state: EditorState;
  onChange: (next: EditorState) => void;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
}

export function CollectionEditorModal({
  otherWhatsNewCards = [],
  state,
  onChange,
  onClose,
  onSave,
  saving,
}: EditorModalProps) {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [lookups, setLookups] = useState<Record<string, LookupValue[]>>({});
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [testCount, setTestCount] = useState<number | null>(null);
  const [testing, setTesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [articlePickerOpen, setArticlePickerOpen] = useState(false);
  /**
   * The article `state.articleId` names, for the card. Held here rather than on
   * the editor state because it is display only — a collection saves the id and
   * nothing else, and putting the document on the state would mean re-opening a
   * collection looked like an edit.
   */
  const [linkedArticle, setLinkedArticle] = useState<Article | null>(null);
  const [articleLoading, setArticleLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadKeyRef = useRef<string>(state.id ?? `new-${Date.now()}`);

  const isLanding = state.targetType === 'landing';
  const isWhatsNew = state.targetType === 'whatsNew';
  /** Landing and What's New both ship as filter cards with a required fallback search. */
  const hasFallback = needsSearchFallback(state.targetType);
  const [whatsNewTitle, setWhatsNewTitle] = useState<string | null>(null);

  // What's New: the name is the configured title, locked (memo Q8).
  useEffect(() => {
    if (!isWhatsNew || whatsNewTitle !== null) return;
    let cancelled = false;
    getWhatsNewTitle()
      .then((t) => !cancelled && setWhatsNewTitle(t))
      .catch((err) => console.warn("Failed to read the What's New title:", err));
    return () => {
      cancelled = true;
    };
  }, [isWhatsNew, whatsNewTitle]);
  useEffect(() => {
    if (isWhatsNew && whatsNewTitle !== null && state.name !== whatsNewTitle) {
      onChange({ ...state, name: whatsNewTitle });
    }
  }, [isWhatsNew, whatsNewTitle, state, onChange]);

  useEffect(() => {
    const id = state.articleId.trim();
    if (!id || linkedArticle?.id === id) return;
    let cancelled = false;
    setArticleLoading(true);
    getArticleById(id)
      .then((article) => {
        if (!cancelled && article) setLinkedArticle(article);
      })
      .catch((err) => {
        // The card falls back to the raw id, which is all the field showed before.
        console.error('Failed to load linked article:', err);
      })
      .finally(() => {
        if (!cancelled) setArticleLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [state.articleId, linkedArticle]);

  useEffect(() => {
    setTestCount(null);
  }, [
    state.facetSelections,
    state.caseSize.min,
    state.caseSize.max,
    state.waterResistance.min,
    state.waterResistance.max,
    state.query,
  ]);

  const handleTestFilters = async () => {
    setTesting(true);
    try {
      const facetFilters = buildFacetFilters(state.facetSelections);
      const numericFilters = buildNumericFilters(state);
      const res = await searchGeneral({
        query: state.query.trim(),
        facetFilters,
        numericFilters,
        limit: 0,
      });
      setTestCount(res.nbHits);
    } catch (err) {
      console.error('Failed to test filters:', err);
      toast.error('Failed to test filters');
      setTestCount(null);
    } finally {
      setTesting(false);
    }
  };

  /**
   * Copy what the linked article already says into the blank landing fields.
   * Fill-empty only — an admin who has written their own body keeps it.
   */
  const handlePrefillFromArticle = async () => {
    const id = state.articleId.trim();
    if (!id) return;
    setPrefilling(true);
    try {
      const article =
        linkedArticle?.id === id ? linkedArticle : await getArticleById(id);
      if (!article) {
        toast.error('No article with that id');
        return;
      }

      const next = { ...state };
      const filled: string[] = [];

      const excerpt = article.excerpt?.trim();
      if (excerpt && !next.landingBodyHtml.trim()) {
        next.landingBodyHtml = `<p>${excerpt}</p>`;
        filled.push('body');
      }
      if (excerpt && !next.description.trim()) {
        next.description = excerpt;
        filled.push('description');
      }
      // Articles carry video only as header media, already normalised to a
      // YouTube embed URL by the article editor.
      if (
        article.headerMedia?.type === 'video' &&
        article.headerMedia.url &&
        !next.videoUrl.trim()
      ) {
        next.videoUrl = article.headerMedia.url;
        filled.push('video');
      }

      if (filled.length === 0) {
        toast('Nothing to fill — those fields are already set', { icon: 'ℹ️' });
        return;
      }
      onChange(next);
      toast.success(`Filled ${filled.join(', ')} from "${article.title}"`);
    } catch (err) {
      console.error('Failed to load article:', err);
      toast.error('Failed to load article');
    } finally {
      setPrefilling(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    setUploading(true);
    try {
      const { downloadUrl } = await uploadCuratedCollectionImage(
        state.id ?? uploadKeyRef.current,
        file,
        file.name,
      );
      onChange({ ...state, imageUrl: downloadUrl });
      toast.success('Image uploaded');
    } catch (err) {
      console.error('Failed to upload image:', err);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOptionsLoading(true);
      try {
        const lookupKeys = Object.values(LOOKUP_COLLECTION_BY_FACET) as string[];
        const [brandsRes, ...lookupRes] = await Promise.all([
          getWatchBrands(),
          ...lookupKeys.map((k) => getLookupCollection(k)),
        ]);
        if (cancelled) return;
        setBrands(brandsRes.sort((a, b) => a.name.localeCompare(b.name)));
        const byKey: Record<string, LookupValue[]> = {};
        lookupKeys.forEach((k, i) => {
          byKey[k] = (lookupRes[i] || [])
            .filter((v) => v.isActive !== false)
            .sort((a, b) =>
              (a.displayName || a.value).localeCompare(b.displayName || b.value),
            );
        });
        setLookups(byKey);
      } catch (err) {
        console.error('Failed to load filter options:', err);
        toast.error('Failed to load filter options');
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const update = <K extends keyof EditorState>(key: K, value: EditorState[K]) => {
    onChange({ ...state, [key]: value });
  };

  const addFacet = (key: FacetKey, value: string) => {
    if (!value) return;
    const already = state.facetSelections.some(
      (s) => s.key === key && s.value === value,
    );
    if (already) return;
    onChange({
      ...state,
      facetSelections: [...state.facetSelections, { key, value }],
    });
  };

  const removeFacet = (idx: number) => {
    onChange({
      ...state,
      facetSelections: state.facetSelections.filter((_, i) => i !== idx),
    });
  };

  const updateRange = (
    field: 'caseSize' | 'waterResistance',
    side: 'min' | 'max',
    value: string,
  ) => {
    onChange({
      ...state,
      [field]: { ...state[field], [side]: value },
    });
  };

  const labelForFacet = (key: FacetKey, value: string): string => {
    if (key === 'brandId') {
      return brands.find((b) => b.id === value)?.name ?? value;
    }
    const lookupKey = LOOKUP_COLLECTION_BY_FACET[key];
    if (!lookupKey) return value;
    const found = lookups[lookupKey]?.find((v) => v.value === value);
    return found?.displayName || found?.value || value;
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={state.id ? 'Edit Collection' : 'New Collection'}
      size="xl"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={state.name}
              onChange={(e) => update('name', e.target.value)}
              disabled={isWhatsNew}
              className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm ${
                isWhatsNew ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
              }`}
              placeholder="e.g. Pilot Watches"
            />
            {isWhatsNew && (
              <p className="text-xs text-gray-500 mt-1">
                Locked to the What&rsquo;s New title — change it in Configuration →
                RTDB Configs → What&rsquo;s New.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort Order
            </label>
            <input
              type="number"
              value={state.sortOrder}
              onChange={(e) => update('sortOrder', Number(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/*
          Destination decides which half of this form matters, so it sits above
          the fields it governs rather than reading as one more filter beside
          them. Content source is deliberately absent from the pair: a landing
          card always ships as `type: 'filter'` (see `editorToPayload`), so the
          control is inert in that mode and is hidden below when landing.
        */}
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">
            Destination
          </span>
          <div
            role="radiogroup"
            aria-label="Destination"
            className="grid grid-cols-3 gap-3"
          >
            <DestinationTile
              label="Search results"
              description="Tapping the card runs the search below."
              selected={!isLanding && !isWhatsNew}
              onSelect={() => update('targetType', 'search')}
            />
            <DestinationTile
              label="Landing page"
              description="Tapping the card opens a page you write here."
              selected={isLanding}
              onSelect={() => update('targetType', 'landing')}
            />
            <DestinationTile
              label="What's New"
              description="Opens the What's New page — built from its config, not from this card."
              selected={isWhatsNew}
              onSelect={() => update('targetType', 'whatsNew')}
            />
          </div>
          {isWhatsNew && otherWhatsNewCards.length > 0 && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-2 mt-2">
              Another collection already opens What&rsquo;s New ({otherWhatsNewCards.join(', ')}).
              The carousel would show both — keep one.
            </p>
          )}
          {isWhatsNew && (
            <p className="text-xs text-gray-500 mt-2">
              The app shows this card only while What&rsquo;s New is enabled and
              visible to the viewer and something new qualifies; its subtitle is
              the live count. The image, overlay and title colour below are yours;
              with no image, the app uses the page&rsquo;s banner.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={state.description}
            onChange={(e) => update('description', e.target.value)}
            rows={2}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Optional short description"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Header Image
          </label>
          {state.imageUrl.trim() && (
            <div className="mb-2">
              <img
                src={state.imageUrl.trim()}
                alt="Collection preview"
                className="w-[200px] h-auto max-h-40 object-cover rounded border border-gray-200 bg-gray-50"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
                onLoad={(e) => {
                  (e.target as HTMLImageElement).style.display = 'block';
                }}
              />
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(file);
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-3 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              {uploading ? 'Uploading…' : state.imageUrl.trim() ? 'Change Image' : 'Upload Image'}
            </button>
            <span className="text-xs text-gray-400">or paste URL</span>
            <input
              type="text"
              value={state.imageUrl}
              onChange={(e) => update('imageUrl', e.target.value)}
              className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="https://…"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title Color
          </label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={state.titleColor || '#FFFFFF'}
              onChange={(e) => update('titleColor', e.target.value)}
              className="h-10 w-14 rounded border border-gray-300 cursor-pointer"
            />
            <input
              type="text"
              value={state.titleColor || '#FFFFFF'}
              onChange={(e) => update('titleColor', e.target.value)}
              className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
              placeholder="#FFFFFF"
            />
            <span className="text-xs text-gray-500">Color of title text on the card</span>
          </div>
          <label className="flex items-center gap-2 text-sm mt-2">
            <input
              type="checkbox"
              checked={state.showOverlay}
              onChange={(e) => update('showOverlay', e.target.checked)}
            />
            Show dark overlay behind title
          </label>
        </div>

        <div className="flex items-start gap-8 flex-wrap">
          {!hasFallback && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content source
              </label>
              <div className="flex flex-col gap-1">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={state.type === 'filter'}
                    onChange={() => update('type', 'filter')}
                  />
                  Filter (auto-populated)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    checked={state.type === 'manual'}
                    onChange={() => update('type', 'manual')}
                  />
                  Manual (curated list)
                </label>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={state.isActive && !state.testMode}
                disabled={state.testMode}
                onChange={(e) => update('isActive', e.target.checked)}
              />
              <span className={state.testMode ? 'text-gray-400' : undefined}>
                Active
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm mt-1">
              <input
                type="checkbox"
                checked={state.testMode}
                onChange={(e) => update('testMode', e.target.checked)}
              />
              Test mode (visible only to admin users)
            </label>
            {state.testMode && (
              <p className="text-xs text-gray-500 mt-1">
                Preview on your device without publishing to end users. Uncheck
                to make live.
              </p>
            )}
          </div>
        </div>

        {isLanding && (
          <p className="text-xs text-gray-500 -mt-2">
            Tapping this card opens a landing page. Older app versions fall
            back to the search below — set at least one filter so those users
            still land somewhere useful.
          </p>
        )}

        {isWhatsNew && (
          <p className="text-xs text-gray-500 -mt-2">
            App versions from before What&rsquo;s New still show this card and
            open the search below instead — set at least one filter so those
            users land somewhere useful.
          </p>
        )}

        {(state.type === 'filter' || hasFallback) && (
          <div className="space-y-4 border-t pt-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-700">Filter Criteria</h4>
              {hasFallback && (
                <p className="text-xs text-amber-700 mt-1">
                  Older app versions fall back to this search — required.
                </p>
              )}
            </div>

            {optionsLoading ? (
              <p className="text-xs text-gray-400">Loading filter options…</p>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <FacetPicker
                    facet="brandId"
                    label={FACET_LABELS.brandId}
                    options={brands.map((b) => ({ value: b.id, label: b.name }))}
                    onAdd={addFacet}
                    placeholder="Select a brand…"
                    searchable
                  />
                  {(Object.keys(LOOKUP_COLLECTION_BY_FACET) as FacetKey[]).map(
                    (facetKey) => {
                      const lookupKey = LOOKUP_COLLECTION_BY_FACET[facetKey]!;
                      const values = lookups[lookupKey] || [];
                      return (
                        <FacetPicker
                          key={facetKey}
                          facet={facetKey}
                          label={FACET_LABELS[facetKey]}
                          options={values.map((v) => ({
                            value: v.value,
                            label: v.displayName || v.value,
                          }))}
                          onAdd={addFacet}
                          placeholder={`Select ${FACET_LABELS[facetKey].toLowerCase()}…`}
                        />
                      );
                    },
                  )}
                </div>

                {state.facetSelections.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Selected filters
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {state.facetSelections.map((sel, idx) => (
                        <span
                          key={`${sel.key}:${sel.value}:${idx}`}
                          className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs rounded-full pl-3 pr-1.5 py-1 border border-blue-100"
                        >
                          <span className="font-medium">
                            {FACET_LABELS[sel.key]}:
                          </span>
                          <span>{labelForFacet(sel.key, sel.value)}</span>
                          <button
                            onClick={() => removeFacet(idx)}
                            className="ml-0.5 w-4 h-4 inline-flex items-center justify-center rounded-full text-blue-600 hover:bg-blue-200"
                            aria-label="Remove"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Case Size (mm)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={state.caseSize.min}
                    onChange={(e) =>
                      updateRange('caseSize', 'min', e.target.value)
                    }
                    placeholder="Min"
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  />
                  <span className="text-gray-400 text-sm">to</span>
                  <input
                    type="number"
                    value={state.caseSize.max}
                    onChange={(e) =>
                      updateRange('caseSize', 'max', e.target.value)
                    }
                    placeholder="Max"
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Water Resistance (m)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={state.waterResistance.min}
                    onChange={(e) =>
                      updateRange('waterResistance', 'min', e.target.value)
                    }
                    placeholder="Min"
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  />
                  <span className="text-gray-400 text-sm">to</span>
                  <input
                    type="number"
                    value={state.waterResistance.max}
                    onChange={(e) =>
                      updateRange('waterResistance', 'max', e.target.value)
                    }
                    placeholder="Max"
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Query (optional)
              </label>
              <input
                type="text"
                value={state.query}
                onChange={(e) => update('query', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Free-text search query"
              />
            </div>

            <div className="flex items-center gap-3 pt-1">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleTestFilters}
                disabled={testing}
              >
                {testing ? 'Testing…' : 'Test Filters'}
              </Button>
              {testing && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600" />
              )}
              {!testing && testCount !== null && (
                <span
                  className={`text-sm font-medium ${
                    testCount > 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {testCount > 0
                    ? `${testCount.toLocaleString()} reference${testCount === 1 ? '' : 's'} match`
                    : 'No matches'}
                </span>
              )}
            </div>
          </div>
        )}

        {((state.type === 'manual' && !isWhatsNew) || isLanding) && (
          <ManualReferencePicker
            references={state.manualReferences}
            onChange={(next) => update('manualReferences', next)}
            max={isLanding ? LANDING_REFERENCE_CAP : undefined}
            brands={brands}
          />
        )}

        {/*
          Not inside the Landing Page block: a search-mode card renders the
          linked article as a subtle chip on its Explore banner, so the picker
          belongs to both destinations. Pre-fill stays landing-only below —
          two of the three fields it writes are erased on a search-mode save.
        */}
        {!isWhatsNew && (
        <div className="space-y-4 border-t pt-4">
          <h4 className="text-sm font-semibold text-gray-700">Article</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Article (optional)
            </label>
            {state.articleId.trim() ? (
              <div className="flex items-center gap-3 border border-gray-200 rounded-lg p-3">
                <ArticleThumbnail
                  url={linkedArticle?.thumbnailUrl}
                  alt={linkedArticle?.title || state.articleId}
                  className="w-12 h-12"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {linkedArticle?.title ||
                      (articleLoading ? 'Loading article…' : 'Unknown article')}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {linkedArticle ? articleSubtitle(linkedArticle) : ''}
                  </div>
                  <div className="text-[11px] text-gray-400 font-mono truncate">
                    {state.articleId}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isLanding && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handlePrefillFromArticle}
                      disabled={prefilling}
                      loading={prefilling}
                    >
                      Pre-fill from article
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setArticlePickerOpen(true)}
                  >
                    Change
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setLinkedArticle(null);
                      update('articleId', '');
                    }}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setArticlePickerOpen(true)}
              >
                Choose Article
              </Button>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Optional. On landing pages, shown as a prominent article card. On
              Explore banners, shown as a subtle chip.
              {isLanding && ' Pre-fill only touches fields you have left blank.'}
            </p>
          </div>

          <ArticlePickerModal
            isOpen={articlePickerOpen}
            onClose={() => setArticlePickerOpen(false)}
            selectedId={state.articleId.trim() || undefined}
            onSelect={(article) => {
              setLinkedArticle(article);
              update('articleId', article.id);
            }}
          />
        </div>
        )}

        {isLanding && (
          <div className="space-y-4 border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-700">Landing Page</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Video URL (optional)
              </label>
              <input
                type="text"
                value={state.videoUrl}
                onChange={(e) => update('videoUrl', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="https://www.youtube.com/watch?v=…"
              />
              <p className="text-xs text-gray-500 mt-1">
                YouTube only. Saved as an embed URL.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Body
              </label>
              <RichTextEditor
                value={state.landingBodyHtml}
                onChange={(html) => update('landingBodyHtml', html)}
                minHeight={300}
                entityLinks
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Requires App Min Version (optional)
              </label>
              <input
                type="text"
                value={state.requiresAppMinVersion}
                onChange={(e) => update('requiresAppMinVersion', e.target.value)}
                className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                placeholder="11.2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Older app versions fall back to the search below when tapping
                this card.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={onSave} loading={saving}>
            {state.id ? 'Save Changes' : 'Create Collection'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

interface FacetPickerProps {
  facet: FacetKey;
  label: string;
  options: { value: string; label: string }[];
  onAdd: (facet: FacetKey, value: string) => void;
  placeholder: string;
  searchable?: boolean;
}

function FacetPicker({
  facet,
  label,
  options,
  onAdd,
  placeholder,
  searchable = false,
}: FacetPickerProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const isSelectingRef = useRef(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options.slice(0, 100);
    return options
      .filter((o) => o.label.toLowerCase().includes(q))
      .slice(0, 100);
  }, [options, search]);

  if (!searchable) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        <select
          value=""
          onChange={(e) => {
            if (e.target.value) {
              onAdd(facet, e.target.value);
              e.target.value = '';
            }
          }}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        type="text"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() =>
          setTimeout(() => {
            if (!isSelectingRef.current) setOpen(false);
          }, 150)
        }
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filtered.map((o) => (
            <button
              key={o.value}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                isSelectingRef.current = true;
                onAdd(facet, o.value);
                setSearch('');
                setOpen(false);
                requestAnimationFrame(() => {
                  isSelectingRef.current = false;
                });
              }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface ManualReferencePickerProps {
  references: ManualReference[];
  onChange: (next: ManualReference[]) => void;
  /** Landing pages render a carousel and cap the list; search results do not. */
  max?: number;
  /** Brand id → display name, for the rows and the picker's brand narrow. */
  brands: WatchBrand[];
}

function ManualReferencePicker({
  references,
  onChange,
  max,
  brands,
}: ManualReferencePickerProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  /**
   * Labels and pictures for references that arrived as bare ids. `referenceIds[]`
   * on the saved document stores ids only, so reopening a collection used to
   * render a wall of raw ids where the curator had picked recognisable watches.
   * Held separately from `references` rather than merged into it, so re-opening
   * an editor never marks it dirty.
   */
  const [labels, setLabels] = useState<
    Map<string, WatchReferenceAlgoliaHit>
  >(new Map());

  /**
   * Ids already sent to Algolia. A reference the index cannot resolve — deleted,
   * or one whose brand has been evicted from the index — never gains a label,
   * and without this it would be re-requested forever: the resolve hands back a
   * fresh Map, `labels` changes identity, `unlabelledIds` recomputes to the same
   * unresolved id, and the effect fires again.
   */
  const requestedIds = useRef<Set<string>>(new Set());

  const unlabelledIds = useMemo(
    () =>
      references
        .filter((r) => !labels.has(r.id) && !requestedIds.current.has(r.id))
        .map((r) => r.id),
    [references, labels],
  );

  useEffect(() => {
    if (unlabelledIds.length === 0) return;
    let cancelled = false;
    unlabelledIds.forEach((id) => requestedIds.current.add(id));
    getReferencesByObjectIds(unlabelledIds)
      .then((found) => {
        if (cancelled || found.size === 0) return;
        setLabels((prev) => {
          const next = new Map(prev);
          found.forEach((hit, id) => next.set(id, hit));
          return next;
        });
      })
      .catch((err) => {
        // The row falls back to the raw id, which is what it showed before.
        console.error('Failed to resolve reference labels:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [unlabelledIds]);

  const selectedIds = useMemo(
    () => new Set(references.map((r) => r.id)),
    [references],
  );

  const brandNames = useMemo(
    () => new Map(brands.map((b) => [b.id, b.name])),
    [brands],
  );

  const brandOptions = useMemo(
    () => brands.map((b) => ({ id: b.id, name: b.name })),
    [brands],
  );

  const atCap = max !== undefined && references.length >= max;
  const remaining = max === undefined ? undefined : max - references.length;

  const removeReference = (id: string) => {
    onChange(references.filter((r) => r.id !== id));
  };

  const moveReferenceBy = (index: number, delta: number) => {
    const next = moveReference(references, index, delta);
    if (next !== references) onChange(next);
  };

  return (
    <div className="space-y-3 border-t pt-4">
      <div className="flex items-baseline justify-between gap-3">
        <h4 className="text-sm font-semibold text-gray-700">References</h4>
        {max !== undefined && (
          <span
            className={`text-xs ${atCap ? 'text-amber-600 font-medium' : 'text-gray-500'}`}
          >
            {references.length} / {max} references
          </span>
        )}
      </div>

      {atCap && (
        <p className="text-xs text-amber-600">
          Maximum of {max} references on a landing page. Remove one to add
          another.
        </p>
      )}

      <Button
        variant="secondary"
        size="sm"
        onClick={() => setPickerOpen(true)}
        disabled={atCap}
      >
        Add References
      </Button>

      <ReferencePickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAdd={(added) => onChange([...references, ...added])}
        existingIds={selectedIds}
        brandNames={brandNames}
        brandOptions={brandOptions}
        remaining={remaining}
      />

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          {references.length === 0
            ? 'No references added yet'
            : max !== undefined
              ? 'Shown on the landing page in this order'
              : `${references.length} reference${references.length === 1 ? '' : 's'}`}
        </label>
        <div className="space-y-2">
          {references.map((ref, index) => (
            <SelectedReferenceRow
              key={ref.id}
              reference={ref}
              resolved={labels.get(ref.id)}
              brandNames={brandNames}
              canMoveUp={index > 0}
              canMoveDown={index < references.length - 1}
              onMoveUp={() => moveReferenceBy(index, -1)}
              onMoveDown={() => moveReferenceBy(index, 1)}
              onRemove={() => removeReference(ref.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The reorder arrows. Disabled rather than hidden at the ends of the list, so
 * the row keeps a constant width and the arrows stay in the same column all
 * the way down.
 */
const MOVE_BUTTON_CLASS =
  'w-6 h-6 inline-flex items-center justify-center rounded border border-gray-200 ' +
  'text-gray-600 leading-none hover:bg-gray-100 ' +
  'disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent';

interface SelectedReferenceRowProps {
  reference: ManualReference;
  /** The hydrated hit for a reference that was saved as a bare id. */
  resolved?: WatchReferenceAlgoliaHit;
  brandNames: Map<string, string>;
  /** False on the first row — there is nowhere above to move to. */
  canMoveUp: boolean;
  /** False on the last row. */
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function SelectedReferenceRow({
  reference,
  resolved,
  brandNames,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRemove,
}: SelectedReferenceRowProps) {
  const imageUrl = useResolvedHitImage(resolved?.imageUrl);
  const label = reference.reference || resolved?.reference || reference.id;
  const model = reference.model || resolved?.model;
  const brandId = reference.brandId || resolved?.brandId;
  const collection = resolved?.collection;
  const sublabel = [brandId ? brandNames.get(brandId) || brandId : '', collection]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex items-center gap-2 border border-gray-200 rounded-lg p-2 bg-white">
      <ReferenceThumbnail url={imageUrl} alt={label} className="w-10 h-10" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900 truncate">
          {label}
          {model && <span className="font-normal text-gray-600"> — {model}</span>}
        </div>
        {sublabel && (
          <div className="text-xs text-gray-500 truncate">{sublabel}</div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!canMoveUp}
          className={MOVE_BUTTON_CLASS}
          aria-label={`Move ${label} up`}
          title="Move up"
        >
          ↑
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!canMoveDown}
          className={MOVE_BUTTON_CLASS}
          aria-label={`Move ${label} down`}
          title="Move down"
        >
          ↓
        </button>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="w-5 h-5 shrink-0 inline-flex items-center justify-center rounded-full text-gray-600 hover:bg-gray-200"
        aria-label={`Remove ${label}`}
      >
        ×
      </button>
    </div>
  );
}

interface DestinationTileProps {
  label: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}

/** One half of the destination pair, sized to be read before the fields below it. */
function DestinationTile({
  label,
  description,
  selected,
  onSelect,
}: DestinationTileProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={`text-left rounded-lg border-2 px-4 py-3 transition-colors ${
        selected
          ? 'border-brand-600 bg-brand-50'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <span
        className={`block text-sm font-semibold ${
          selected ? 'text-brand-700' : 'text-gray-700'
        }`}
      >
        {label}
      </span>
      <span className="block text-xs text-gray-500 mt-0.5">{description}</span>
    </button>
  );
}
